import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { convertPdfToImage, dataURLtoFile } from '@/lib/pdfToImage';

export interface DetectedPolicy {
  document_type_id: string;
  document_type_name: string;
  document_number?: string;
  coverage_amount?: string;
  issue_date?: string;
  expiry_date?: string;
  issuing_authority?: string;
  holder_name?: string;
  confidence: number;
  additional_info?: Record<string, any>;
  selected: boolean;
}

export interface DetectionResult {
  detected_policies: DetectedPolicy[];
  overall_confidence: number;
}

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function useMultiTypeDetection() {
  const [isDetecting, setIsDetecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const detectDocumentTypes = async (
    file: File,
    availableDocumentTypes: any[] = []
  ): Promise<DetectionResult | null> => {
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a PDF or image file');
      return null;
    }

    setIsDetecting(true);
    setError(null);

    try {
      let fileToAnalyze = file;
      
      // Convert PDF to image if needed
      if (file.type === 'application/pdf') {
        toast.info('Converting PDF for analysis...', {
          description: 'This may take a moment...'
        });
        
        try {
          const imageDataUrl = await convertPdfToImage(file);
          fileToAnalyze = dataURLtoFile(imageDataUrl, `${file.name}.png`);
        } catch (conversionError) {
          toast.error('PDF conversion failed', {
            description: 'Please try uploading an image instead'
          });
          return null;
        }
      }

      const base64Data = await fileToBase64(fileToAnalyze);

      const { data, error: invokeError } = await supabase.functions.invoke('detect-document-types', {
        body: {
          imageData: base64Data,
          availableDocumentTypes,
        },
      });

      if (invokeError) throw invokeError;

      if (!data || !data.detected_policies || data.detected_policies.length === 0) {
        toast.warning('No document types detected', {
          description: 'Please select the document type manually.',
        });
        return null;
      }

      // Mark all policies as selected by default
      const policiesWithSelection = data.detected_policies.map((policy: DetectedPolicy) => ({
        ...policy,
        selected: true,
      }));

      const result: DetectionResult = {
        detected_policies: policiesWithSelection,
        overall_confidence: data.overall_confidence,
      };

      // Show success message based on number detected
      if (result.detected_policies.length === 1) {
        toast.success('Document analyzed', {
          description: `Found 1 policy: ${result.detected_policies[0].document_type_name}`,
        });
      } else {
        toast.success('Multiple policies detected', {
          description: `Found ${result.detected_policies.length} policies in this document`,
        });
      }

      return result;
    } catch (err: any) {
      console.error('Detection error:', err);
      const errorMessage = err.message || 'Failed to detect document types';
      setError(errorMessage);
      toast.error('Detection failed', {
        description: errorMessage,
      });
      return null;
    } finally {
      setIsDetecting(false);
    }
  };

  const clearError = () => setError(null);

  return {
    detectDocumentTypes,
    isDetecting,
    error,
    clearError,
  };
}
